/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

<template>
  <section
    v-if="loaded"
    aria-labelledby="recordingH1"
  >
    <h1
      id="recordingH1"
      style="font-size: 18px;"
    >
      VoiceVibes A Bigtincan Company
    </h1>
    <h2
      id="many-ways-to-get-started"
      style="font-size: 14px; font-weight: 400;"
    >
      There are many different ways to get started.
    </h2>
    <span style="font-size: 12px">
      Provide a recording below then return to the RESULTS tab above to see your results.
    </span><br><br>
    <h2
      id="manage-nav"
      class="sr-only"
    >
      Recording Navigation
    </h2>
    <nav aria-labelledby="manage-nav">
      <ul
        id="evaluate-top-tabs"
        class="nav nav-tabs"
      >
        <li
          :class="{ active: tab === 'record' }"
        >
          <a
            id="recordlink"
            ref="recordlink"
            href="#"
            @click.prevent="loadTab('record')"
            @keypress="loadTab('record', $event)"
          >
            Record <br>&nbsp;
          </a>
        </li>
        <li
          :class="{ active: tab === 'app' }"
        >
          <a
            id="applink"
            ref="applink"
            href="#"
            @click.prevent="loadTab('app')"
            @keypress="loadTab('app', $event)"
          >
            Recorder <br>App
          </a>
        </li>
        <li
          :class="{ active: tab === 'upload' }"
        >
          <a
            id="uploadlink"
            ref="uploadlink"
            href="#"
            @click.prevent="loadTab('upload')"
            @keypress="loadTab('upload', $event)"
          >
            Upload <br>File
          </a>
        </li>
        <li
          :class="{ active: tab === 'url' }"
        >
          <a
            id="urllink"
            ref="urllink"
            href="#"
            @click.prevent="loadTab('url')"
            @keypress="loadTab('url', $event)"
          >
            Upload <br>URL
          </a>
        </li>
        <li
          :class="{ active: tab === 'zoom' }"
        >
          <a
            v-if="hasZoom"
            id="zoomlink"
            ref="zoomlink"
            :href="zoomUrl"
            target="_blank"
          >
            Upload <br>ZOOM
          </a>
        </li>
      </ul>
    </nav>
    <div
      v-show="tab === 'record'"
      class="tab-well"
    >
      <record-form />
    </div>
    <div
      v-show="tab === 'app'"
      class="tab-well"
    >
      <h2
        id="appheader"
        ref="appheader"
        class="sr-only"
      >
        Mobile App Details
      </h2>
      <app-tab />
    </div>
    <div
      v-show="tab === 'upload'"
      class="tab-well"
    >
      <upload-form />
    </div>
    <div
      v-show="tab === 'url'"
      class="tab-well"
    >
      <url-form />
    </div>
  </section>
</template>

<style lang="less" scoped>
  #evaluate-top-tabs li {
    a br {
      display: none;
    }
    width: 110px;
    @media (max-width: 600px) {
      width: 55px;
      a br {
        display: block;
      }
      a {
        font-size: 8px;
      }
    }
  }
  .navbar-header {
    background-color: #F26724;
    width: 120%;
    margin-bottom: 10px;
  }

  .navbar-brand {
    padding-top: 5px;
    padding-bottom: 5px;
    img {
      height: 40px;
    }
  }
  #recordingH1 {
    margin-top: 0 !important;
    padding-top: 0;
  }
</style>

<script>
import RecordForm from '../forms/RecordForm';
import UploadForm from '../forms/UploadForm';
import UrlForm from '../forms/UrlForm';
import AppTab from '../forms/AppTab';

export default {
  components: { RecordForm, AppTab, UploadForm, UrlForm },
  data() {
    return {
      tab: 'record',
      loaded: false,
    };
  },
  computed: {
    zoomUrl() {
      const base = window.location.origin + '/zoom-recordings';
      const params = this.$router.currentRoute.query.assignmentId ?
        `?assignmentId=${this.$router.currentRoute.query.assignmentId}` : '';

      return base + params;
    },
    isFreeTrial() {
      return !!(this.user && this.user.metadata && this.user.metadata.freeTrial);
    },
    hasZoom() {
      const org = this.$store.getters.org;
      return (org && typeof org.settings !== 'undefined' &&
        org.settings.Zoom_Integration);
    },
    user() {
      return this.$store.getters.user;
    },
    assignmentsEnabled() {
      const org = this.$store.getters.org;
      return (org && typeof org.settings !== 'undefined' && org.settings.Assignments);
    },
  },
  mounted() {
    // Support auto-selecting a tab via router.
    if (this.$route.params.tab && this.$route.params.tab !== this.tab) {
      this.tab = this.$route.params.tab;
      this.$route.params.tab = null;
    }
  },
  created() {
    if (!this.assignmentsEnabled) {
      this.loaded = true;
    } else {
      const promises = [
        this.$store.dispatch('getAssignedAssignments'),
        this.$store.dispatch('listRecordings'),
      ];
      window.Promise.all(promises).then(() => this.loaded = true);
    }
  },
  methods: {
    loadTab(identifier, evt) {
      if (evt && evt.key && evt.key === 'Tab') {
        // Firefox bug fix
        return true;
      }

      if (identifier === 'zoom') {
        return this.$router.replace({name: 'zoom-recordings'});
      }

      const link = this.$refs[identifier.replace(/ /g, '') + 'link'];
      link.blur();
      this.$nextTick(() => {
        const header = document.getElementById(identifier.replace(/ /g, '') + 'header');
        if (header) {
          header.focus();
        }
      });

      this.tab = identifier;
      this.$announcer.set(`The ${identifier} tab has been activated. The associated page content is now available below.`);
    },
  }
};
</script>
